import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/shared/components';
import { useApi, type AddressSuggestionResponse } from '@/shared/api';

interface PropertyAddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (suggestion: AddressSuggestionResponse) => void;
}

/**
 * Adress-Combobox mit eindeutigen Zuständen (F3):
 *  - `committed` (Ref): der zuletzt gewählte bzw. initiale Wert. Solange der Query
 *    ihm entspricht, wird NICHT gesucht und keine Liste geöffnet — Fokus öffnet also
 *    keine committed alte Liste, und ein vorbelegtes Feld sucht beim Mount nicht.
 *  - `dismissed` (Ref): Escape/Blur unterdrücken das Wieder-Öffnen bis zur nächsten Suche.
 *  - AbortController + Effekt-Flag: ältere Requests überschreiben niemals neuere.
 *  - Gesucht wird nur, was im Feld steht. Postleitzahl und Ort aus den Formularfeldern gehören
 *    beim Bearbeiten zur alten Adresse und lenkten die Suche nach einer neuen dorthin.
 */
export function PropertyAddressAutocomplete({
  value,
  onChange,
  onSelect,
}: PropertyAddressAutocompleteProps) {
  const api = useApi();
  const listId = useId();
  const [suggestions, setSuggestions] = useState<AddressSuggestionResponse[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const committed = useRef(value);
  const dismissed = useRef(false);
  // Suchfunktion über einen Ref halten → der Effekt hängt NICHT an der `api`-Identität
  // (ein instabiles useApi() würde sonst den Effekt bei jedem Render neu feuern).
  const searchRef = useRef(api.addressVerification.search);
  searchRef.current = api.addressVerification.search;

  useEffect(() => {
    const query = value.trim();
    if (query === committed.current.trim() || query.length < 3) {
      // Idempotent zurücksetzen (gleiche Referenz zurückgeben) → kein Re-Render-Loop.
      setSuggestions((prev) => (prev.length ? [] : prev));
      setActiveIndex((prev) => (prev === -1 ? prev : -1));
      setOpen((prev) => (prev ? false : prev));
      return;
    }
    dismissed.current = false;
    let active = true;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const result = await searchRef.current({ query }, controller.signal);
        if (!active) return;
        setSuggestions(result);
        setActiveIndex(-1);
        setOpen(result.length > 0 && !dismissed.current);
      } catch {
        if (active) {
          setSuggestions([]);
          setOpen(false);
        }
      }
    }, 350);
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [value]);

  const choose = (suggestion: AddressSuggestionResponse) => {
    committed.current = suggestion.label;
    dismissed.current = true;
    setSuggestions([]);
    setActiveIndex(-1);
    setOpen(false);
    onSelect(suggestion);
  };

  const canOpen = () =>
    value.trim() !== committed.current.trim() && suggestions.length > 0 && !dismissed.current;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown' && canOpen()) {
        event.preventDefault();
        setOpen(true);
        setActiveIndex(0);
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      const active = suggestions[activeIndex];
      if (active) choose(active);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      dismissed.current = true;
      setActiveIndex(-1);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setOpen(canOpen())}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={handleKeyDown}
        leftIcon={<MapPin size={14} strokeWidth={2} />}
        placeholder="Adresse suchen"
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="border-line shadow-card absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-white p-1"
        >
          {suggestions.map((suggestion, index) => (
            <li
              id={`${listId}-${index}`}
              key={`${listId}-opt-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className="text-ink hover:bg-card-alt aria-selected:bg-card-alt cursor-pointer rounded px-3 py-2 text-sm"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(suggestion)}
            >
              <div className="flex items-start justify-between gap-3">
                <span>{suggestion.label}</span>
                {suggestion.source === 'ExistingObject' && (
                  <span className="bg-sage/15 text-sage shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                    Bereits vorhanden
                  </span>
                )}
              </div>
              {suggestion.source === 'ExistingObject' &&
                suggestion.visiblePropertyCount != null && (
                  <span className="text-ink-faint mt-1 block text-xs">
                    {suggestion.visiblePropertyCount}{' '}
                    {suggestion.visiblePropertyCount === 1 ? 'Apartment' : 'Apartments'}
                  </span>
                )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
